import { z } from 'zod';
export var CaptchaType;
(function (CaptchaType) {
    CaptchaType["RecaptchaEnterprise"] = "RecaptchaEnterprise";
    CaptchaType["Turnstile"] = "Turnstile";
    CaptchaType["Altcha"] = "Altcha";
    CaptchaType["Cap"] = "Cap";
})(CaptchaType || (CaptchaType = {}));
export var RecaptchaEnterpriseMode;
(function (RecaptchaEnterpriseMode) {
    RecaptchaEnterpriseMode["Invisible"] = "invisible";
    RecaptchaEnterpriseMode["Checkbox"] = "checkbox";
})(RecaptchaEnterpriseMode || (RecaptchaEnterpriseMode = {}));
export const turnstileConfigGuard = z.object({
    type: z.literal(CaptchaType.Turnstile),
    siteKey: z.string(),
    secretKey: z.string(),
});
export const recaptchaEnterpriseConfigGuard = z.object({
    type: z.literal(CaptchaType.RecaptchaEnterprise),
    siteKey: z.string(),
    secretKey: z.string(),
    projectId: z.string(),
    domain: z.string().optional(),
    mode: z.nativeEnum(RecaptchaEnterpriseMode).optional(),
    scoreThreshold: z.number().min(0).max(1).optional(),
});
/**
 * Config of a self-hosted Cap Standalone instance.
 *
 * @see https://capjs.js.org/guide/standalone/
 */
export const capConfigGuard = z.object({
    type: z.literal(CaptchaType.Cap),
    /** The public base URL of the Cap Standalone instance, e.g. `https://cap.example.com`. */
    endpoint: z
        .string()
        .url()
        .refine((value) => ['http:', 'https:'].includes(new URL(value).protocol), {
        message: 'The endpoint must be an HTTP(S) URL.',
    }),
    siteKey: z.string(),
    secretKey: z.string(),
});
// URLs are admin-controlled, but credentials and redirects must not escape to another origin.
const altchaUrlGuard = z
    .string()
    .url()
    .max(2048)
    .refine((value) => {
    try {
        const url = new URL(value);
        return (['https:', 'http:'].includes(url.protocol) &&
            !url.username &&
            !url.password &&
            !url.hash &&
            !url.search &&
            (url.protocol === 'https:' || ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname)));
    }
    catch {
        return false;
    }
}, 'Use HTTPS (HTTP is allowed only on loopback), without credentials, query or fragment');
export const altchaConfigGuard = z.object({
    type: z.literal(CaptchaType.Altcha),
    challengeUrl: altchaUrlGuard,
    verifyUrl: altchaUrlGuard,
    verificationKey: z
        .string()
        .min(32)
        .max(256)
        .regex(/^[\w-]+$/)
        .refine((value) => value.trim() === value),
});
export const captchaPublicConfigGuard = z.discriminatedUnion('type', [
    capConfigGuard.omit({ secretKey: true }),
    turnstileConfigGuard.omit({ secretKey: true }),
    recaptchaEnterpriseConfigGuard.omit({ secretKey: true, projectId: true }),
    altchaConfigGuard.omit({ verificationKey: true, verifyUrl: true }),
]);
export const captchaConfigGuard = z.discriminatedUnion('type', [
    turnstileConfigGuard,
    recaptchaEnterpriseConfigGuard,
    altchaConfigGuard,
    capConfigGuard,
]);
